/**
 * 机位视口可视化 + 实时取景画中画（D-31 / P0-5b 决策 2、4、5）
 *
 * CameraGizmos：每个机位 = 机身小盒 + 视锥线框 + 名称标签 chip。
 *  · 选中：视锥半透明填充 + 指向注视目标的蓝色箭头 + 标签蓝描边；
 *  · 取景中：标签带琥珀"取景"角标；
 *  · 交互（修竞品"机身本体不可拾取"的缺陷）：机身/视锥/标签都可单击选中、
 *    双击直接进入该机位视角（决策 5 的增强项）。
 *
 * CameraPiP：选中机位且处于自由视角时，视口右上角 scissor 渲染实时取景缩略图。
 *  · 用"单 canvas 分区渲染"实现（three 经典多视口手法），不建第二个 WebGL
 *    上下文——双上下文共享场景是已知雷区，这里绕开；
 *  · useFrame(priority=1) 接管渲染：先画主画面再画 PiP，顺序不可反。
 */
import { useEffect, useMemo, useState } from 'react';
import { Html, Line, TransformControls } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { PerspectiveCamera, Quaternion } from 'three';
import type { Group } from 'three';

import {
  CAMERA_GIZMO_ACTIVE_COLOR,
  CAMERA_GIZMO_COLOR,
  CAMERA_GIZMO_SELECTED_COLOR,
  GRID_SNAP_STEP_M,
  type DirectorCamera,
  type DirectorProject,
} from '../../model/director';
import { computeLookAtQuaternion, resolveTargetPoint } from './cameraMath';
import { useDirectorStore } from './store';

const FRUSTUM_DEPTH = 0.55; // 图标尺寸（米），表达"朝哪拍"，不代表真实构图深度
const ASPECT = 16 / 9;

function frustumSize(fovDeg: number): { w: number; h: number } {
  const h = 2 * FRUSTUM_DEPTH * Math.tan((fovDeg * Math.PI) / 360);
  return { w: h * ASPECT, h };
}

function frustumPoints(fovDeg: number): Array<[number, number, number]> {
  const { w, h } = frustumSize(fovDeg);
  const corners: Array<[number, number, number]> = [
    [-w / 2, h / 2, -FRUSTUM_DEPTH],
    [w / 2, h / 2, -FRUSTUM_DEPTH],
    [w / 2, -h / 2, -FRUSTUM_DEPTH],
    [-w / 2, -h / 2, -FRUSTUM_DEPTH],
  ];
  const apex: [number, number, number] = [0, 0, 0];
  const pts: Array<[number, number, number]> = [];
  for (const c of corners) {
    pts.push(apex, c); // 四条棱
  }
  pts.push(corners[0]!, corners[1]!, corners[1]!, corners[2]!, corners[2]!, corners[3]!, corners[3]!, corners[0]!);
  return pts;
}

function CameraGizmo({
  cam,
  project,
  selected,
  active,
}: {
  cam: DirectorCamera;
  project: DirectorProject;
  selected: boolean;
  active: boolean;
}) {
  const target = resolveTargetPoint(project, cam) ?? cam.target.point;
  const quat = computeLookAtQuaternion(cam.position, target);
  const color = selected ? CAMERA_GIZMO_SELECTED_COLOR : active ? CAMERA_GIZMO_ACTIVE_COLOR : CAMERA_GIZMO_COLOR;
  const pts = useMemo(() => frustumPoints(cam.fov), [cam.fov]);
  const far = useMemo(() => frustumSize(cam.fov), [cam.fov]);

  return (
    <>
      <group
        position={[cam.position[0], cam.position[1], cam.position[2]]}
        quaternion={quat ?? new Quaternion()}
        onClick={(e) => {
          e.stopPropagation();
          useDirectorStore.getState().selectCamera(cam.id);
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          const s = useDirectorStore.getState();
          s.selectCamera(cam.id);
          s.setActiveCamera(cam.id);
          s.setViewMode('camera');
        }}
      >
        {/* 机身 */}
        <mesh position={[0, 0, 0.11]}>
          <boxGeometry args={[0.2, 0.14, 0.18]} />
          <meshBasicMaterial color={color} transparent opacity={selected || active ? 0.95 : 0.75} />
        </mesh>
        {/* 视锥线框 */}
        <Line points={pts} segments color={color} lineWidth={selected ? 2 : 1} transparent opacity={0.9} />
        {/* 选中：远平面半透明填充（取证：竞品选中时机位视锥有填充面） */}
        {selected && (
          <mesh position={[0, 0, -FRUSTUM_DEPTH]}>
            <planeGeometry args={[far.w, far.h]} />
            <meshBasicMaterial color={CAMERA_GIZMO_SELECTED_COLOR} transparent opacity={0.18} side={2} />
          </mesh>
        )}
        {/* 名称标签 chip */}
        <Html position={[0, 0.22, 0]} center distanceFactor={7} zIndexRange={[20, 0]}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              useDirectorStore.getState().selectCamera(cam.id);
            }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              const s = useDirectorStore.getState();
              s.selectCamera(cam.id);
              s.setActiveCamera(cam.id);
              s.setViewMode('camera');
            }}
            className={`whitespace-nowrap rounded-control border px-1.5 py-0.5 text-[11px] transition-colors duration-[120ms] ease-out ${
              selected
                ? 'border-state-info bg-surface-overlay text-state-info'
                : active
                  ? 'border-accent bg-surface-overlay text-accent-ink'
                  : 'border-surface-line bg-surface-raised text-ink-secondary'
            }`}
            style={active ? { backgroundColor: CAMERA_GIZMO_ACTIVE_COLOR } : undefined}
          >
            {cam.name}
            {active ? ' · 取景' : ''}
          </button>
        </Html>
      </group>
      {/* 选中：机身→注视目标的蓝色箭头（取证 Q7，仅选中时可见） */}
      {selected && (
        <Line
          points={[[cam.position[0], cam.position[1], cam.position[2]], [target[0], target[1], target[2]]]}
          color={CAMERA_GIZMO_SELECTED_COLOR}
          lineWidth={2}
          dashed
          dashSize={0.18}
          gapSize={0.1}
        />
      )}
    </>
  );
}

export function CameraGizmos() {
  const project = useDirectorStore((s) => s.project);
  const selectedCameraId = useDirectorStore((s) => s.selectedCameraId);
  return (
    <>
      {project.cameras.map((c) => (
        <CameraGizmo
          key={c.id}
          cam={c}
          project={project}
          selected={c.id === selectedCameraId}
          active={c.id === project.activeCameraId}
        />
      ))}
    </>
  );
}

/** 机位变换控件（第六轮实测补：选中机位后无可拖 gizmo 的缺口）。
 *  机位没有 rotation（朝向由 target 反解），故复用视口 V/R/F 语义映射为两个平移把手：
 *   · V(translate) → 拖机身锚点改 position；
 *   · R(rotate)    → 拖注视锚点（小球）改 target.point，即"调角度"；
 *   · F(scale)     → 机位无缩放，不显示。
 *  锚点用两个 group 承载（位置由 store 数据驱动，拖拽 onObjectChange 回写同值→幂等，
 *  与对象 Gizmo 同一套防打架约定）。拖注视点会把手动机位从 follow 解绑为 point（预期行为）。 */
export function CameraGizmoControls({ mode }: { mode: 'translate' | 'rotate' | 'scale' }) {
  const cam = useDirectorStore(
    (s) => s.project.cameras.find((c) => c.id === s.selectedCameraId) ?? null,
  );
  const project = useDirectorStore((s) => s.project);
  const gridSnap = useDirectorStore((s) => s.project.scene.gridSnap);
  const viewMode = useDirectorStore((s) => s.viewMode);
  const updateCamera = useDirectorStore((s) => s.updateCamera);
  const [bodyObj, setBodyObj] = useState<Group | null>(null);
  const [aimObj, setAimObj] = useState<Group | null>(null);

  const target = cam !== null ? resolveTargetPoint(project, cam) ?? cam.target.point : null;
  const camId = cam !== null ? cam.id : null;

  // 位置命令式同步（关键：锚点 group 不写 JSX position prop，否则 R3F 每帧回写
  // 与 TransformControls 拖拽打架→机位"无限跳远"）。用回调 ref 存进 state，
  // deps 含 bodyObj/aimObj——从相机视角切回自由视角时锚点重挂，会重新触发同步。
  useEffect(() => {
    if (bodyObj !== null && cam !== null) {
      bodyObj.position.set(cam.position[0], cam.position[1], cam.position[2]);
    }
  }, [bodyObj, cam]);
  useEffect(() => {
    if (aimObj !== null && target !== null) {
      aimObj.position.set(target[0], target[1], target[2]);
    }
  }, [aimObj, target]);

  // 仅自由视角可编辑：相机视角下你正"透过"机位看，拖它无意义且会与取景互采。
  if (cam === null || target === null || camId === null || viewMode !== 'free') {
    return null;
  }
  const id: string = camId;
  const attach = mode === 'translate' ? bodyObj : mode === 'rotate' ? aimObj : null;

  function commit(): void {
    if (mode === 'translate' && bodyObj !== null) {
      const p = bodyObj.position;
      updateCamera(id, { position: [p.x, p.y, p.z] });
    } else if (mode === 'rotate' && aimObj !== null) {
      const p = aimObj.position;
      updateCamera(id, { target: { mode: 'point', point: [p.x, p.y, p.z], followId: null } });
    }
  }

  return (
    <>
      {/* 机身锚点：不可见，位置由 effect 命令式写入，供 V 模式拖拽 */}
      <group ref={setBodyObj} />
      {/* 注视锚点：可见小球，供 R 模式拖拽改角度 */}
      <group ref={setAimObj}>
        <mesh>
          <sphereGeometry args={[0.06, 12, 12]} />
          <meshBasicMaterial color={CAMERA_GIZMO_SELECTED_COLOR} />
        </mesh>
      </group>
      {attach !== null && (
        <TransformControls
          object={attach}
          mode="translate"
          size={0.9}
          translationSnap={gridSnap ? GRID_SNAP_STEP_M : null}
          onObjectChange={commit}
        />
      )}
    </>
  );
}

/** 实时取景缩略图（scissor 分区渲染，单 WebGL 上下文） */
export function CameraPiP() {
  const pipCam = useMemo(() => new PerspectiveCamera(50, ASPECT, 0.05, 500), []);

  useFrame((state) => {
    const { gl, scene, camera, size } = state;
    // priority>0 接管渲染：必须先画主画面
    gl.autoClear = true;
    gl.setScissorTest(false);
    gl.setViewport(0, 0, size.width, size.height);
    gl.render(scene, camera);

    const s = useDirectorStore.getState();
    const selCam = s.project.cameras.find((c) => c.id === s.selectedCameraId);
    if (selCam === undefined || s.viewMode === 'camera') {
      return;
    }
    const target = resolveTargetPoint(s.project, selCam) ?? selCam.target.point;
    pipCam.fov = selCam.fov;
    pipCam.aspect = ASPECT;
    pipCam.position.set(selCam.position[0], selCam.position[1], selCam.position[2]);
    pipCam.lookAt(target[0], target[1], target[2]);
    pipCam.updateProjectionMatrix();

    const w = Math.min(240, Math.max(140, Math.floor(size.width * 0.24)));
    const h = Math.round(w / ASPECT);
    const mx = 12;
    const my = 44; // 顶部留标签条空间（页面层 HTML 标签与此对齐）
    gl.setScissorTest(true);
    gl.setScissor(size.width - w - mx, size.height - h - my, w, h);
    gl.setViewport(size.width - w - mx, size.height - h - my, w, h);
    gl.render(scene, pipCam);
    gl.setScissorTest(false);
    gl.setViewport(0, 0, size.width, size.height);
  }, 1);

  return null;
}

/** PiP 角标文案已移至 viewProbe.ts（react-refresh 要组件文件只导出组件） */
